import { useCallback, useState } from 'react'

import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { toast, useIsUnmounted } from '@sales-center/components'

import { CloseWinOpportunityInput } from '../typings'
import { closeWinOpportunity, getOpportunityIsCloseable } from '../api'
import { isAPIError } from '@user/store/services/crud'
import { getOpportunityDetail } from '..'
import { updateLoadingState } from '@sales-center/common'

type State = {
  isCloseable: boolean | 'pending'
  closeWinStatus: 'idle' | 'pending' | 'success' | 'fail'
}

export function useCloseWinOpportunity(id: number) {
  const dispatch = useAppDispatch()

  const isUnmounted = useIsUnmounted()

  const [state, setState] = useState<State>(() => ({
    isCloseable: 'pending',
    closeWinStatus: 'idle',
  }))

  const canCloseWin = useCallback(async () => {
    setState({
      isCloseable: 'pending',
      closeWinStatus: 'idle',
    })

    const result = await dispatch(getOpportunityIsCloseable(id))

    if (isUnmounted()) return

    if (getOpportunityIsCloseable.fulfilled.match(result)) {
      const { isCloseable } = result.payload

      setState({
        isCloseable,
        closeWinStatus: 'idle',
      })
    } else {
      setState({
        isCloseable: false,
        closeWinStatus: 'idle',
      })

      const { payload } = result
      if (isAPIError(payload)) toast.error(payload.Message)
    }
  }, [dispatch, id, isUnmounted])

  const onCloseWin = useCallback(
    async (input: CloseWinOpportunityInput) => {
      setState((s) => ({ ...s, closeWinStatus: 'pending' }))
      dispatch(updateLoadingState('visible'))
      const result = await dispatch(closeWinOpportunity(input))

      if (isUnmounted()) return

      if (closeWinOpportunity.fulfilled.match(result)) {
        setState((s) => ({ ...s, closeWinStatus: 'success' }))
        dispatch(getOpportunityDetail(input.opportunityId))
        dispatch(updateLoadingState('hidden'))
        toast.success('Opportunity closed successfully')
      } else {
        setState((s) => ({ ...s, closeWinStatus: 'fail' }))
        dispatch(updateLoadingState('hidden'))
        toast.success('Unable to close Opportunity')
      }
    },
    [dispatch, isUnmounted],
  )

  return {
    ...state,
    onCloseWin,
    canCloseWin,
  }
}
